import { useCallback, useRef, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import {
  saveConstructionEstimatorProjectBrief,
  type EstimateLoadData, type StandaloneProjectBrief,
} from '../api/constructionPriceEstimatorFunctions';
import { safeActionError } from './ConstructionPricePublicProjection';

/** Serialize blur/fill saves without carrying their revision fence across flows. */
export function useConstructionProjectBriefSave({
  flowId, canSave, generation, activeFlowId, text, saved, setLoad, setText, setError,
}: {
  flowId: string;
  canSave: boolean;
  generation: MutableRefObject<number>;
  activeFlowId: MutableRefObject<string>;
  text: MutableRefObject<string>;
  saved: MutableRefObject<StandaloneProjectBrief>;
  setLoad: Dispatch<SetStateAction<EstimateLoadData | null>>;
  setText: Dispatch<SetStateAction<string>>;
  setError: Dispatch<SetStateAction<string | null>>;
}) {
  const canSaveRef = useRef(canSave);
  canSaveRef.current = canSave;
  const queue = useRef<Promise<StandaloneProjectBrief | null>>(Promise.resolve(null));
  const queueGeneration = useRef(generation.current);
  if (queueGeneration.current !== generation.current) {
    queueGeneration.current = generation.current;
    queue.current = Promise.resolve(null);
  }
  return useCallback((): Promise<StandaloneProjectBrief | null> => {
    const started = generation.current;
    const isCurrent = () => activeFlowId.current === flowId && generation.current === started;
    if (!canSaveRef.current || !isCurrent()) return Promise.resolve(null);
    const snapshot = text.current.trim();
    const task = queue.current.catch(() => null).then(async () => {
      if (!isCurrent() || !canSaveRef.current) return null;
      if (snapshot === saved.current.text) return saved.current;
      try {
        const next = await saveConstructionEstimatorProjectBrief(flowId, saved.current.revision, snapshot);
        if (!isCurrent()) return null;
        saved.current = next;
        setLoad((current) => current ? { ...current, project_brief: next } : current);
        // Preserve newer owner input until its queued save is accepted.
        if (text.current.trim() === snapshot) setText(next.text);
        return next;
      } catch (caught) {
        if (!isCurrent()) return null;
        setError(safeActionError(caught, 'Description could not be saved.'));
        throw caught;
      }
    });
    queue.current = task;
    return task;
  }, [activeFlowId, canSave, flowId, generation, saved, setError, setLoad, setText, text]);
}
